<template>
  <div>
    <v-dialog
      :value="show"
      persistent
      lazy
      :width="$store.state.common.mediumDialogWidth"
      @input="(val) => $emit('update:show', val)"
    >
      <v-card>
        <v-card-title class="pb-0 d-block">
          <v-layout align-center>
            <v-layout
              align-center
              wrap
            >
              <div class="headline mb-2">
                {{
                  inCreateMode
                    ? $t('menuCategoriesCreateDialogTitle')
                    : $t('menuCategoriesEditDialogTitle')
                }}
              </div>
            </v-layout>
            <v-btn
              class="ml-auto mr-0 my-0"
              flat
              icon
              :title="$t('close')"
              @click="close()"
            >
              <v-icon>close</v-icon>
            </v-btn>
          </v-layout>
          <div
            v-if="showCategoriesLink"
            class="subheading"
          >
            <router-link :to="{name: 'menucategories'}">
              {{ $t('menuCategoriesCreateEditDialogLinkText') }}
            </router-link>
          </div>
        </v-card-title>
        <v-card-text>
          <!-- Name -->
          <v-text-field
            ref="menu-category-name-textfield"
            v-model="payload.name"
            data-cy="menu-category-dialog-name-input"
            :placeholder="$t('name')"
            counter="128"
            :error-messages="[
              inCreateMode && !nameNotAlreadyUsed ? $t('nameAlreadyInUse') : '',
            ]"
            @keyup.enter="save()"
          />
          <!-- Details -->
          <v-textarea
            v-if="showMenuCategoryDetails"
            v-model="payload.details"
            class="mt-2"
            clearable
            no-resize
            rows="3"
            :label="$t('menuCategoryDetails')"
          />
          <!-- TimeFrames -->
          <timeframe-controls
            v-if="showMenuCategoryTimeframes"
            ref="timeframecontrols"
            switch-hint="timeFrameSwitchHintForMenuCategory"
            class="mt-2"
          />
        </v-card-text>
        <v-card-actions>
          <v-btn
            data-cy="menu-category-dialog-confirm-btn"
            round
            flat
            color="msr"
            class="ml-auto"
            :disabled="!formIsValid"
            @click="save()"
          >
            {{ $t('save') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import TimeframeControls from '@/components/TimeframeControls';

function createDefaultPayload() {
  return {
    id: null,
    name: '',
    details: '',
    timeframes: [],
  };
}

export default {
  components: {
    TimeframeControls,
  },
  props: {
    show: {
      type: Boolean,
      required: true,
    },
    menuCategories: {
      type: Array[Object],
      required: true,
    },
    editedMenuCategory: {
      type: Object,
      default: null,
    },
    showMenuCategoryDetails: {
      type: Boolean,
      default: false,
    },
    showMenuCategoryTimeframes: {
      type: Boolean,
      default: false,
    },
    showCategoriesLink: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      payload: createDefaultPayload(),
    };
  },
  computed: {
    inCreateMode() {
      return this.editedMenuCategory == null;
    },
    inEditMode() {
      return this.editedMenuCategory != null;
    },
    nameNotEmpty() {
      return !!this.payload.name && this.payload.name.trim() != '';
    },
    nameNotAlreadyUsed() {
      return !this.menuCategories
        .map((category) => category.name.toLowerCase())
        .includes(this.payload.name.trim().toLowerCase());
    },
    formIsValid() {
      if (this.inCreateMode) {
        return this.nameNotEmpty && this.nameNotAlreadyUsed;
      }
      return this.nameNotEmpty;
    },
  },
  watch: {
    show: function (show) {
      if (!show) {
        this.reset();
      } else {
        if (this.inEditMode) {
          this.populatePayloadAndUIFromMenuCategory(this.editedMenuCategory);
        }
        this.$nextTick(() => {
          this.$refs['menu-category-name-textfield'].focus();
        });
      }
    },
  },
  methods: {
    populatePayloadAndUIFromMenuCategory(menuCategory) {
      this.payload = createDefaultPayload();
      // Ensure all state is copied to avoid unwanted modifications.
      this.payload.id = menuCategory.id;
      this.payload.name = menuCategory.name;
      this.payload.details = menuCategory.details;
      this.payload.timeframes = (menuCategory.timeframes || []).slice();

      this.$nextTick(() => {
        this.$refs.timeframecontrols.initializeWith(this.payload.timeframes);
      });
    },
    reset() {
      this.payload = createDefaultPayload();
    },
    close() {
      this.$emit('update:show', false);
    },
    save() {
      if (!this.formIsValid) {
        return;
      }

      // TODO: The timeframe-controls widget should not work based on
      //   $refs and calling methods, but instead be refactored to work
      //   with props/.sync so state is automatically updated. For now,
      //   use the 'old' way though.
      const timeframeControls = this.$refs.timeframecontrols;
      const timeframeIds = timeframeControls.selectedTimeframes.map(
        (x) => x.id
      );
      this.payload.timeframes = timeframeIds;

      this.$emit('save', this.payload);
    },
  },
};
</script>



// WEBPACK FOOTER //
// src/components/Menu/CreateEditMenuCategoryDialog.vue